import { DownOutlined, ShopOutlined } from '@ant-design/icons';
import { Grid, type MenuProps, Space } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMultipleDealerIds } from '../../components/contexts/DealerContextManager';
import Dropdown from '../../components/Dropdown';
import { Action } from './ui';

const ALL_DEALERS = 'ALL';

type DealerMenuProps = {
    isDisplay?: boolean;
};

const ConsoleDealerMenu = ({ isDisplay = false }: DealerMenuProps) => {
    const [isVisible, setVisible] = useState(false);
    const { t } = useTranslation(['consoleLayout']);
    const { dealerIds, toggle, dealerOptions, toggleAll, displayDropdown } = useMultipleDealerIds();

    const screens = Grid.useBreakpoint();
    const display = displayDropdown || isDisplay;
    // Allow dealerIds to be empty
    const onDealerClick = useCallback<MenuProps['onClick']>(
        info => {
            if (info.key === ALL_DEALERS) {
                toggleAll();
            } else {
                toggle(info.key);
            }
            setVisible(true);
        },
        [toggle, toggleAll]
    );

    const items = useMemo(
        () => [
            { label: t('consoleLayout:rightContent.allDealers'), key: ALL_DEALERS },
            ...(dealerOptions?.map(item => ({ label: item.label, key: item.value })) ?? []),
        ],
        [dealerOptions, t]
    );

    const getDealerLabel = () => {
        if (dealerIds.length === dealerOptions.length) {
            return screens.md ? (
                t('consoleLayout:rightContent.allDealerSelected', { total: dealerIds.length })
            ) : (
                <ShopOutlined />
            );
        }

        if (dealerIds.length > 1) {
            return t('consoleLayout:rightContent.dealerSelected', { total: dealerIds.length });
        }

        if (dealerIds.length === 1) {
            return dealerOptions.find(item => item.value === dealerIds[0])?.label;
        }

        return t('consoleLayout:rightContent.selectDealer');
    };

    // If available dealers is below 2, render empty menu
    if (dealerOptions.length < 2) {
        return null;
    }

    return (
        display && (
            <Dropdown
                menu={{
                    items,
                    onClick: onDealerClick,
                    selectedKeys: dealerIds.length === dealerOptions.length ? [ALL_DEALERS] : dealerIds,
                    selectable: true,
                }}
                onOpenChange={setVisible}
                open={isVisible}
                placement="bottomRight"
            >
                <Action>
                    <Space>
                        {getDealerLabel()}
                        <DownOutlined style={{ fontSize: 9 }} />
                    </Space>
                </Action>
            </Dropdown>
        )
    );
};

export default ConsoleDealerMenu;
